import { z } from "zod";

/** Alinhado ao backend `ProductJourneyFieldWidgetEnum`. */
export const productJourneyFieldWidgetSchema = z.enum([
  "text",
  "textarea",
  "email",
  "tel",
  "number",
  "currency",
  "date",
  "select",
  "combobox",
  "checkbox",
  "switch",
]);

export const productJourneyFieldOptionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
});

export const productJourneyFieldSchema = z.object({
  key: z
    .string()
    .regex(/^[a-z][a-z0-9_]*$/, {
      message: "validationMessages.journeyConfig.fieldKeySnakeCase",
    }),
  label: z.string().min(1),
  widget: productJourneyFieldWidgetSchema,
  integrationType: z.string().optional(),
  fillable: z.boolean(),
  required: z.boolean().optional(),
  placeholder: z.string().optional(),
  helpText: z.string().optional(),
  options: z.array(productJourneyFieldOptionSchema).optional(),
  defaultValue: z
    .union([z.string(), z.number(), z.boolean(), z.null()])
    .optional(),
});

export const productJourneyStepSchema = z.object({
  id: z.string().min(1),
  title: z.string().optional(),
  order: z.number().int().min(0),
  fieldKeys: z.array(z.string().min(1)).min(1),
});

/** Conteúdo de `product_journey_configs.config` (jsonb). */
export const productJourneyConfigPayloadSchema = z
  .object({
    schemaVersion: z.number().int().min(1),
    fields: z.array(productJourneyFieldSchema).min(1),
    steps: z.array(productJourneyStepSchema).min(1),
  })
  .superRefine((data, ctx) => {
    const fieldKeys = new Set<string>();
    for (const f of data.fields) {
      if (fieldKeys.has(f.key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "validationMessages.journeyConfig.duplicateFieldKey",
          path: ["fields"],
        });
      }
      fieldKeys.add(f.key);
    }
    const stepOrders = new Set<number>();
    const keysPlaced = new Set<string>();

    for (const step of data.steps) {
      if (stepOrders.has(step.order)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "validationMessages.journeyConfig.duplicateStepOrder",
          path: ["steps"],
        });
      }
      stepOrders.add(step.order);

      const seenInStep = new Set<string>();
      for (const fk of step.fieldKeys) {
        if (seenInStep.has(fk)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "validationMessages.journeyConfig.duplicateFieldInStep",
            path: ["steps"],
          });
        }
        seenInStep.add(fk);
        if (!fieldKeys.has(fk)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "validationMessages.journeyConfig.unknownFieldKey",
            path: ["steps"],
          });
        }
        if (keysPlaced.has(fk)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "validationMessages.journeyConfig.fieldInMultipleSteps",
            path: ["steps"],
          });
        }
        keysPlaced.add(fk);
      }
    }

    for (const k of fieldKeys) {
      if (!keysPlaced.has(k)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "validationMessages.journeyConfig.fieldNotInAnyStep",
          path: ["fields"],
        });
      }
    }
  });

export const createProductJourneyConfigFormSchema = z.object({
  product_id: z.string().uuid(),
  config: productJourneyConfigPayloadSchema,
});
